import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Coins } from "lucide-react";
import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  CURRENCY_META,
  SUPPORTED_CURRENCIES,
  useCurrency,
  type Currency,
} from "@/lib/currency";

interface Props {
  variant?: "compact" | "full";
}

export function CurrencySwitcher({ variant = "compact" }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { currency, setCurrency } = useCurrency();

  // Close popover on scroll so it never floats over other UI while the page moves.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", close, { capture: true } as never);
  }, [open]);

  const change = (next: Currency) => {
    setOpen(false);
    setCurrency(next);
  };

  const label = t("common.currency", { defaultValue: "Moneda" });

  if (variant === "full") {
    const idx = SUPPORTED_CURRENCIES.indexOf(currency);
    const pct = 100 / SUPPORTED_CURRENCIES.length;
    return (
      <div className="space-y-2">
        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </span>
        <div className="relative grid grid-cols-3 rounded-full border border-border bg-muted/50 p-1">
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-1 rounded-full bg-primary shadow-[0_6px_16px_-6px_oklch(0.72_0.18_45/0.55)] transition-all duration-300 ease-out"
            style={{ width: `calc(${pct}% - 0.25rem)`, left: `calc(${idx * pct}% + 0.125rem)` }}
          />
          {SUPPORTED_CURRENCIES.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => change(code)}
              className={`relative z-10 flex flex-col items-center gap-0.5 rounded-full py-1.5 text-[11px] font-semibold transition-colors ${
                currency === code ? "text-primary-foreground" : "text-foreground/70 hover:text-foreground"
              }`}
              aria-pressed={currency === code}
            >
              <span className="text-base leading-none">{CURRENCY_META[code].flag}</span>
              <span className="uppercase tracking-wider">{code}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="inline-flex h-10 items-center gap-1.5 rounded-full border border-amber-200/30 bg-black/40 px-3 text-sm font-semibold text-amber-100 shadow-sm transition-all hover:border-amber-200/60 hover:bg-black/60"
        >
          <Coins className="h-4 w-4 text-amber-300" />
          <span className="uppercase tracking-wider">{currency}</span>
          <ChevronDown
            className={`h-3.5 w-3.5 text-amber-200/70 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={10}
        avoidCollisions={true}
        collisionPadding={16}
        className="w-60 overflow-hidden rounded-2xl border border-amber-200/15 bg-[#0a0e14]/95 p-0 text-amber-50 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] backdrop-blur-xl lg:!w-60"
      >
        <div className="border-b border-amber-200/10 px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-200/70">
          {label}
        </div>
        <ul role="listbox" className="p-1.5">
          {SUPPORTED_CURRENCIES.map((code) => {
            const active = currency === code;
            return (
              <li key={code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => change(code)}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                    active
                      ? "bg-amber-400/15 font-semibold text-amber-200"
                      : "text-amber-50/85 hover:bg-white/5 hover:text-amber-100"
                  }`}
                >
                  <span className="text-lg leading-none">{CURRENCY_META[code].flag}</span>
                  <span className="flex-1">
                    <span className="font-semibold uppercase tracking-wider">{code}</span>
                    <span className="ml-1.5 text-xs text-amber-50/60">
                      {CURRENCY_META[code].label}
                    </span>
                  </span>
                  {active && <Check className="h-4 w-4 text-amber-300" />}
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
